// src/components/DashboardLayout.js
import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar/Sidebar";
import BreadCrumb from "../BreadCrumb/BreadCrumb";
import ComponentsWrapper from "../ComponentsWrapper/ComponentsWrapper";
import "./DashboardLayout.css";
import Routespage from "../routes/Routespage";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCodebookData,
  fetchCodebookData13,
  setSelectedCodebookVersion,
} from "../CodeBook/redux/CodeBookSlicer";
import CodebookLoader from "../CodeBook/CodebookLoader";
import { useSingleTabEnforcement } from "./DuplicateTabWarnings/Usesingletabenforcement";
import { getCodebookFromIndexedDB } from "../../utils/indexedDB";

const DashboardLayout = ({ children }) => {
  const [isRowClicked, setIsRowClicked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();
  const storedPermissions = JSON.parse(sessionStorage.getItem("permissions") || "[]");
  const isUSA = process.env.REACT_APP_FLAG_USA;
  const loadStartedRef = useRef(false);
  const isMountedRef = useRef(true);
  const { isBlocked } = useSingleTabEnforcement();

  const {
    isFetched12,
    isFetched13,
    selectedCodebookVersion,
    icd10amIndexData,
    icd10amIndexData13,
    achiIndexData,
    achiIndexData13,
    codeBookLoading,
  } = useSelector((state) => state.codeBook);

  // Check if data is available in Redux
  const isDataInRedux = () => {
    const version = selectedCodebookVersion || (isUSA ? "12th" : "13th");
    
    if (version === "13th") {
      return icd10amIndexData13?.length > 0 && achiIndexData13?.length > 0;
    } else {
      return icd10amIndexData?.length > 0 && achiIndexData?.length > 0;
    }
  };

  // Check if data is available in sessionStorage
  const isDataInSession = () => {
    const version = selectedCodebookVersion || (isUSA ? "12th" : "13th");
    return version === "13th" 
      ? sessionStorage.getItem("isFetched13") === "true"
      : sessionStorage.getItem("isFetched12") === "true";
  };

  // Check IndexedDB for cached data - returns boolean synchronously if possible
  const checkIndexedDB = async (version) => {
    try {
      if (version === "13th") {
        const cached = await getCodebookFromIndexedDB("codebookData13:icd10amIndexData13");
        if (cached) {
          sessionStorage.setItem("isFetched13", "true");
          return true;
        }
      } else {
        const cached = await getCodebookFromIndexedDB("codebookData12:icd10amIndexData");
        if (cached) {
          sessionStorage.setItem("isFetched12", "true");
          return true;
        }
      }
    } catch (error) {
      console.error("Error checking IndexedDB:", error);
    }
    return false;
  };

  useEffect(() => {
    const hasCodebookAccess =
      Object.keys(storedPermissions).includes("Code Accelerate") ||
      Object.keys(storedPermissions).includes("Code Assist") ||
      storedPermissions?.admin === true;

    if (!hasCodebookAccess) {
      setShowLoader(false);
      setIsInitialized(true);
      return;
    }

    const initializeData = async () => {
      if (loadStartedRef.current) return;
      if (!isMountedRef.current) return;

      const defaultVersion = isUSA ? "12th" : "13th";
      if (!selectedCodebookVersion) {
        dispatch(setSelectedCodebookVersion(defaultVersion));
      }

      const version = selectedCodebookVersion || defaultVersion;
      
      if (isDataInSession()) {
        // console.log("Data found in sessionStorage - NO loader");
        setShowLoader(false);
        setIsInitialized(true);
        
        // If data is in sessionStorage but not in Redux, fetch it
        if (!isDataInRedux()) {
          // Trigger fetch to populate Redux
          if (version === "13th" && !isFetched13) {
            dispatch(fetchCodebookData13());
          } else if (version === "12th" && !isFetched12) {
            dispatch(fetchCodebookData());
          }
        }
        return;
      }
      // STEP 2: Check if data is already in Redux
      if (isDataInRedux()) {
        // console.log("Data found in Redux - NO loader");
        setShowLoader(false);
        setIsInitialized(true);
        return;
      }
      // STEP 3: Check IndexedDB directly (async but we still hide loader)
      setIsInitialized(true);
      
      const hasCachedData = await checkIndexedDB(version);
      
      if (hasCachedData) {
        setShowLoader(false);
        
        // Trigger fetch to populate Redux from cache (in background)
        if (version === "13th" && !isFetched13) {
          dispatch(fetchCodebookData13());
        } else if (version === "12th" && !isFetched12) {
          dispatch(fetchCodebookData());
        }
        return;
      }
      // STEP 4: No cache found - show loader and fetch from network
      setShowLoader(true);
      loadStartedRef.current = true;
      await fetchData(version);
    };

    const fetchData = async (version) => {
      try {
        if (version === "13th") {
          await dispatch(fetchCodebookData13()).unwrap();
        } else {
          await dispatch(fetchCodebookData()).unwrap();
        }
        
        if (isMountedRef.current) {
          setShowLoader(false);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Error loading codebook data:", error);
        if (isMountedRef.current) {
          setShowLoader(false);
          setIsInitialized(true);
        }
      }
    };

    initializeData();

    return () => {
      isMountedRef.current = false;
    };
  }, [dispatch, storedPermissions, isUSA, selectedCodebookVersion]);

  // Update loading state when Redux data loads
  useEffect(() => {
    if (isDataInRedux()) {
      setShowLoader(false);
      setIsInitialized(true);
    }
  }, [icd10amIndexData, icd10amIndexData13, achiIndexData, achiIndexData13]);

  const shouldShowLoader = showLoader && 
    !isDataInRedux() && 
    !isDataInSession() &&
    isInitialized;

  // console.log('Loader state:', {
  //   shouldShowLoader,
  //   showLoader,
  //   isDataInRedux: isDataInRedux(),
  //   isDataInSession: isDataInSession(),
  //   isInitialized,
  //   isFetched13,
  //   isFetched12,
  //   codeBookLoading,
  //   dataLength13: icd10amIndexData13?.length,
  //   dataLength12: icd10amIndexData?.length,
  // });

  return (
    <div className="main-wrapper">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div style={{ width: "100%" }} className="right-side-section">
        <BreadCrumb sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <ComponentsWrapper />
        <div style={{ height: "100%", marginTop: "25px", padding: "7px", width: "100%" }}>
          {shouldShowLoader ? (
            <div className="codebook-loading-overlay">
              <CodebookLoader />
            </div>
          ) : (
            <Routespage isRowClicked={isRowClicked} setIsRowClicked={setIsRowClicked} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;